import React, { createContext, useContext, useReducer } from 'react';
import { themes, DEFAULT_THEME } from '../charts/shared/themes';
import {
  deploymentProfile,
  getAllowedThemes,
  sanitizeThemeId,
} from '../config/deploymentProfile';

const REQUIRED_FIELDS = [
  'background', 'plotBackground', 'foreground', 'axisColor', 'gridColor',
  'gridAlpha', 'tickColor', 'labelColor', 'titleColor', 'legendTextColor', 'referenceLineColor',
  'fontHeading', 'fontBody', 'fontLabel', 'fontNumber', 'tickSize', 'labelSize', 'legendSize', 'valueLabelSize',
  'spineWidth', 'barRadius', 'palette', 'paletteSequential', 'ui',
];

const REQUIRED_UI_FIELDS = [
  'bg',
  'bgSecondary',
  'text',
  'textSecondary',
  'border',
  'borderSecondary',
  'hover',
  'accent',
  'accentFg',
  'scrollTrack',
  'scrollThumb',
  'overlayBackdrop',
  'emptyStateText',
  'codeKeyword',
  'codeString',
  'codeComment',
  'codeNumber',
];

function isNonEmptyString(value) {
  return typeof value === 'string' && value.trim().length > 0;
}

function validateStringArray(value, label) {
  if (!Array.isArray(value) || value.length < 3) {
    return `${label} must be an array with at least 3 colors.`;
  }
  const invalidEntry = value.find((item) => !isNonEmptyString(item));
  return invalidEntry ? `${label} must only contain color strings.` : null;
}

function validateThemeDefinition(themeData) {
  if (themeData == null || typeof themeData !== 'object' || Array.isArray(themeData)) {
    return ['Theme JSON must be an object.'];
  }

  const errors = [];

  REQUIRED_FIELDS.forEach((field) => {
    if (!(field in themeData)) errors.push(`Missing field: ${field}`);
  });

  if ('ui' in themeData && (themeData.ui == null || typeof themeData.ui !== 'object' || Array.isArray(themeData.ui))) {
    errors.push('Field "ui" must be an object.');
  }

  if (themeData.ui && typeof themeData.ui === 'object' && !Array.isArray(themeData.ui)) {
    REQUIRED_UI_FIELDS.forEach((field) => {
      if (!(field in themeData.ui)) errors.push(`Missing ui field: ui.${field}`);
    });
  }

  const numericFields = [
    'gridAlpha',
    'tickSize',
    'labelSize',
    'legendSize',
    'valueLabelSize',
    'spineWidth',
    'barRadius',
  ];
  numericFields.forEach((field) => {
    if (field in themeData && !Number.isFinite(themeData[field])) {
      errors.push(`Field "${field}" must be a finite number.`);
    }
  });

  const paletteError = validateStringArray(themeData.palette, 'palette');
  if (paletteError) errors.push(paletteError);

  const sequentialError = validateStringArray(themeData.paletteSequential, 'paletteSequential');
  if (sequentialError) errors.push(sequentialError);

  return errors;
}

function computeResolved(themeId, overrides, allThemes = themes) {
  const base = allThemes[themeId] || themes[DEFAULT_THEME];
  const merged = { ...base, ...overrides };
  // Deep-merge the ui object so individual ui overrides don't clobber the rest
  if (base.ui && overrides.ui) {
    merged.ui = { ...base.ui, ...overrides.ui };
  }
  return merged;
}

const initialState = {
  themeId: sanitizeThemeId(deploymentProfile.defaultThemeId, themes),
  overrides: {},
  customThemes: {},
};

function themeReducer(state, action) {
  switch (action.type) {
    case 'SET_THEME': {
      return {
        ...state,
        themeId: action.payload,
        overrides: {},
      };
    }
    case 'SET_OVERRIDE': {
      return {
        ...state,
        overrides: { ...state.overrides, [action.key]: action.value },
      };
    }
    case 'SET_BULK_OVERRIDES': {
      return {
        ...state,
        overrides: { ...state.overrides, ...action.payload },
      };
    }
    case 'RESET_OVERRIDES': {
      return {
        ...state,
        overrides: {},
      };
    }
    case 'ADD_CUSTOM_THEME': {
      const theme = action.payload;
      const customThemes = { ...state.customThemes, [theme.id]: theme };
      return {
        ...state,
        customThemes,
        themeId: theme.id,
        overrides: {},
      };
    }
    default:
      return state;
  }
}

const ThemeContext = createContext(null);

export function ThemeProvider({ children }) {
  const [state, dispatch] = useReducer(themeReducer, initialState);

  const mergedThemes = deploymentProfile.allowCustomThemes
    ? { ...themes, ...state.customThemes }
    : themes;
  const availableThemes = getAllowedThemes(mergedThemes);
  const activeThemeId = sanitizeThemeId(state.themeId, availableThemes);
  const resolved = computeResolved(activeThemeId, state.overrides, availableThemes);

  const setTheme = (id) => {
    const nextThemeId = sanitizeThemeId(id, availableThemes);
    dispatch({ type: 'SET_THEME', payload: nextThemeId });
  };
  const setOverride = (key, value) => dispatch({ type: 'SET_OVERRIDE', key, value });
  const setBulkOverrides = (obj) => dispatch({ type: 'SET_BULK_OVERRIDES', payload: obj });
  const resetOverrides = () => dispatch({ type: 'RESET_OVERRIDES' });
  const addCustomTheme = (themeData) => {
    if (!deploymentProfile.allowCustomThemes) return;
    dispatch({ type: 'ADD_CUSTOM_THEME', payload: themeData });
  };

  const isLocked = availableThemes[activeThemeId]?.locked ?? false;

  return (
    <ThemeContext.Provider value={{
      ...state,
      themeId: activeThemeId,
      resolved,
      themes,
      allThemes: availableThemes,
      deploymentProfile,
      isLocked,
      setTheme,
      setOverride,
      setBulkOverrides,
      resetOverrides,
      addCustomTheme,
      REQUIRED_FIELDS,
      REQUIRED_UI_FIELDS,
      validateThemeDefinition,
    }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used inside ThemeProvider');
  return ctx;
}

export default ThemeContext;
